import React, { Fragment, useEffect, useState } from "react";
import { Row, Col, Card, Table, Button } from "react-bootstrap";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Select from "react-select";
import CardItem from "./CardItem";
import "./style.scss";
import TotalSalariesModal from "./TotalSalariesModal";
import SalarySlip from "./SalarySlip";
import { Translate } from "../../../Enums/Tranlate";
import MonthDropDown, { months } from "../../../Enums/MonthDropDown";
import YearDropDown from "../../../Enums/YearDropDown";
import Loader from "../../../common/Loader";
import NoData from "../../../common/NoData";
import Pagination from "../../../common/Pagination/Pagination";
import SalariesService from "../../../../services/HRM/SalariesService";
import SalarySettingsService from "../../../../services/HRM/SalarySettingsService";
import ScrollPagination from "../../../common/ScrollPagination";
import EmployeesService from "../../../../services/HRM/EmployeesService";

const TiersTable = ({ tiers, type, lang, onAdd, onRemove, onUpdate }) => (
	<div className="mt-3">
		<div className="d-flex justify-content-between align-items-center mb-2">
			<h6 className="mb-0">
				{type === "late"
					? Translate[lang]?.late_tiers
					: Translate[lang]?.early_leave_tiers}
			</h6>
			<Button variant="outline-primary" size="sm" onClick={() => onAdd(type)}>
				+ {Translate[lang]?.add_tier}
			</Button>
		</div>
		<table className="table table-bordered text-center mb-0">
			<thead className="table-light">
				<tr>
					<th>{Translate[lang]?.from_minutes}</th>
					<th>{Translate[lang]?.to_minutes}</th>
					<th>{Translate[lang]?.deduction_percentage}</th>
					<th></th>
				</tr>
			</thead>
			<tbody>
				{tiers.length === 0 && (
					<tr>
						<td colSpan={4} className="text-muted">
							—
						</td>
					</tr>
				)}
				{tiers.map((tier, i) => (
					<tr key={i}>
						<td>
							<input
								type="number"
								className="form-control form-control-sm"
								value={tier.fromMinutes ?? ""}
								min={0}
								onChange={(e) =>
									onUpdate(type, i, "fromMinutes", e.target.value)
								}
							/>
						</td>
						<td>
							<input
								type="number"
								className="form-control form-control-sm"
								value={tier.toMinutes ?? ""}
								min={0}
								placeholder={Translate[lang]?.unlimited}
								onChange={(e) => onUpdate(type, i, "toMinutes", e.target.value)}
							/>
						</td>
						<td>
							<input
								type="number"
								className="form-control form-control-sm"
								value={tier.deductionPercentage ?? ""}
								min={0}
								max={100}
								onChange={(e) =>
									onUpdate(type, i, "deductionPercentage", e.target.value)
								}
							/>
						</td>
						<td>
							<button
								className="btn btn-danger btn-xs py-1 px-2"
								onClick={() => onRemove(type, i)}>
								<i className="la la-trash" />
							</button>
						</td>
					</tr>
				))}
			</tbody>
		</table>
	</div>
);

const Salaries = () => {
	const tabs = ["payroll_salary", "salary_slip", "salary_settings"];
	const [selectedTab, setSelectedTab] = useState("payroll_salary");
	const [data, setData] = useState([]);
	const lang = useSelector((state) => state.auth?.lang);
	const [params, setParams] = useState({
		month: {
			label: Translate[lang][months[new Date().getMonth()].toLocaleLowerCase()],
			value: new Date().getMonth() + 1,
		},
		year: {
			label: `${new Date().getFullYear()}`,
			value: new Date().getFullYear(),
		},
	});
	const [modal, setModal] = useState(false);
	const [hasData, setHasData] = useState(1);
	const [search, setSearch] = useState(null);
	const [loading, setLoading] = useState(false);
	const [shouldUpdate, setShouldUpdate] = useState(false);
	const salariesService = new SalariesService();
	const Auth = useSelector((state) => state.auth?.auth);
	const isExist = (data) => Auth?.admin?.admin_roles?.includes(data);

	// ── Salary Settings tab ──────────────────────────────────
	const [workingDays, setWorkingDays] = useState("");
	const [savingSalary, setSavingSalary] = useState(false);

	const [attSettings, setAttSettings] = useState({
		lateDeductionEnabled: false,
		earlyLeaveDeductionEnabled: false,
		lateTiers: [],
		earlyLeaveTiers: [],
	});
	const [selectedExcludedEmps, setSelectedExcludedEmps] = useState([]);
	const [loadingSalarySettings, setLoadingSalarySettings] = useState(false);
	const [savingAtt, setSavingAtt] = useState(false);

	useEffect(() => {
		if (selectedTab !== "salary_settings") return;
		setLoadingSalarySettings(true);
		Promise.all([
			new SalarySettingsService().getSettings(),
			new SalarySettingsService().getAttendanceDeductionSettings(),
		])
			.then(([salRes, attRes]) => {
				if (salRes?.status === 200) {
					setWorkingDays(salRes?.data.data?.workingDays ?? "");
				}
				if (attRes?.status === 200) {
					const d = attRes?.data?.data;
					setAttSettings({
						lateDeductionEnabled: d?.lateDeductionEnabled,
						earlyLeaveDeductionEnabled: d?.earlyLeaveDeductionEnabled,
						lateTiers: d.lateTiers || [],
						earlyLeaveTiers: d.earlyLeaveTiers || [],
					});
					const excluded = d.excludedEmployees || [];
					setSelectedExcludedEmps(
						excluded.map(({ id, name }) => ({ value: id, label: name })),
					);
				}
			})
			.finally(() => setLoadingSalarySettings(false));
	}, [selectedTab]);

	const saveSalarySettings = () => {
		setSavingSalary(true);
		new SalarySettingsService()
			.updateSettings({ workingDays: Number(workingDays) })
			.then((res) => {
				if (res?.status === 200)
					toast.success(Translate[lang]?.updated_successfully);
			})
			.finally(() => setSavingSalary(false));
	};

	const saveAttSettings = () => {
		setSavingAtt(true);
		const data = {
			lateDeductionEnabled: attSettings.lateDeductionEnabled,
			earlyLeaveDeductionEnabled: attSettings.earlyLeaveDeductionEnabled,
			lateTiers: attSettings.lateTiers.map((t) => ({
				fromMinutes: t.fromMinutes !== "" ? Number(t.fromMinutes) : null,
				toMinutes:
					t.toMinutes !== "" && t.toMinutes !== null
						? Number(t.toMinutes)
						: null,
				deductionPercentage: Number(t.deductionPercentage),
			})),
			earlyLeaveTiers: attSettings.earlyLeaveTiers.map((t) => ({
				fromMinutes: t.fromMinutes !== "" ? Number(t.fromMinutes) : null,
				toMinutes:
					t.toMinutes !== "" && t.toMinutes !== null
						? Number(t.toMinutes)
						: null,
				deductionPercentage: Number(t.deductionPercentage),
			})),
			excludedEmployeeIds: selectedExcludedEmps.map((e) => e.value),
		};
		new SalarySettingsService()
			.updateAttendanceDeductionSettings(data)
			.then((res) => {
				if (res?.status === 200)
					toast.success(Translate[lang]?.updated_successfully);
			})
			.finally(() => setSavingAtt(false));
	};

	const addTier = (type) => {
		const key = type === "late" ? "lateTiers" : "earlyLeaveTiers";
		setAttSettings((prev) => ({
			...prev,
			[key]: [
				...prev[key],
				{ fromMinutes: "", toMinutes: "", deductionPercentage: "" },
			],
		}));
	};

	const removeTier = (type, index) => {
		const key = type === "late" ? "lateTiers" : "earlyLeaveTiers";
		setAttSettings((prev) => ({
			...prev,
			[key]: prev[key].filter((_, i) => i !== index),
		}));
	};

	const updateTier = (type, index, field, value) => {
		const key = type === "late" ? "lateTiers" : "earlyLeaveTiers";
		setAttSettings((prev) => ({
			...prev,
			[key]: prev[key].map((tier, i) =>
				i === index ? { ...tier, [field]: value } : tier,
			),
		}));
	};
	// ────────────────────────────────────────────────────────

	const changeParams = (e, name) => {
		setParams({ ...params, [name]: e });
		setShouldUpdate((prev) => !prev);
	};

	const printProjects = () => {
		setLoading(true);
		salariesService
			.getList({
				month: params.month.value,
				year: params.year.value,
			})
			.then((res) => {
				if (res?.status === 200) {
					if (res?.data?.data?.data?.length === 0) {
						setLoading(false);
						return;
					}
				}
				setLoading(false);
			})
			.catch(() => setLoading(false));
	};

	const exportToExcel = () => {
		salariesService
			.getList({
				month: params?.month?.value,
				year: params?.year?.value,
			})
			.then((res) => {
				if (res?.status === 200) {
					let response = res?.data?.data?.data;
					// ExportExcel(response)
				}
			});
	};
	return (
		<Fragment>
			<Card className="mb-3">
				<Card.Body className="p-0">
					<div className="tabs-div">
						{tabs?.map((tab, index) => {
							return (
								<p
									key={index}
									className={`${tab === selectedTab ? "activeP" : ""}`}
									onClick={() => setSelectedTab(tab)}>
									{Translate[lang][tab]}
								</p>
							);
						})}
					</div>
				</Card.Body>
			</Card>
			{selectedTab === "payroll_salary" && (
				<>
					<Row>
						<Col lg={12}>
							<Card>
								<Card.Body>
									<Row className="mb-3">
										<Col md={2} sm={5}>
											<MonthDropDown
												params={params}
												changeParams={changeParams}
											/>
										</Col>
										<Col md={2} sm={5}>
											<YearDropDown
												params={params}
												changeParams={changeParams}
											/>
										</Col>
									</Row>
									{loading && (
										<div style={{ height: "300px" }}>
											<Loader />
										</div>
									)}
									{hasData === 1 && !loading && (
										<>
											<Table responsive>
												<thead>
													<tr className="text-center">
														<th>
															<strong>I.D</strong>
														</th>
														<th>
															<strong>{Translate[lang]?.employee_name}</strong>
														</th>
														<th>
															<strong>{Translate[lang]?.department}</strong>
														</th>
														<th>
															<strong>{Translate[lang]?.job_title}</strong>
														</th>
														<th>
															<strong>{Translate[lang]?.salary}</strong>
														</th>
														<th>
															<strong>{Translate[lang]?.payroll_salary}</strong>
														</th>
														<th>
															<strong>{Translate[lang]?.view}</strong>
														</th>
													</tr>
												</thead>

												<tbody className="table-body">
													{data?.map((item, index) => {
														return (
															<CardItem
																index={index}
																key={index}
																item={item}
																month={params.month.value}
																year={params.year.value}
															/>
														);
													})}
												</tbody>
											</Table>
										</>
									)}
									{hasData === 0 && (
										<div className="text-center">
											<NoData />
										</div>
									)}
									<Pagination
										setData={setData}
										service={salariesService}
										shouldUpdate={shouldUpdate}
										setHasData={setHasData}
										setLoading={setLoading}
										search={search}
										param={{
											month: params.month.value,
											year: params.year.value,
										}}
									/>
								</Card.Body>
							</Card>
						</Col>
					</Row>
				</>
			)}

			{selectedTab === "salary_slip" && <SalarySlip />}

			{selectedTab === "salary_settings" && (
				<>
					{/* Salary Settings */}
					<Card className="mb-3">
						<Card.Body>
							<h5 className="mb-3">{Translate[lang]?.salary_settings}</h5>
							<Row>
								<Col md={4}>
									<label className="text-label">
										{Translate[lang]?.working_days}
									</label>
									<input
										type="number"
										className="form-control"
										value={workingDays}
										min={1}
										onChange={(e) => setWorkingDays(e.target.value)}
									/>
								</Col>
							</Row>
							<div className="mt-3">
								<Button
									variant="primary"
									onClick={saveSalarySettings}
									disabled={savingSalary}>
									{savingSalary ? (
										<span className="spinner-border spinner-border-sm" />
									) : (
										Translate[lang]?.save
									)}
								</Button>
							</div>
						</Card.Body>
					</Card>

					{/* Attendance Deduction Settings */}
					<Card>
						<Card.Body>
							<h5 className="mb-3">
								{Translate[lang]?.attendance_deduction_settings}
							</h5>
							{loadingSalarySettings ? (
								<div style={{ height: "200px" }}>
									<Loader />
								</div>
							) : (
								<>
									<Row>
										<Col md={6}>
											<div className="mb-3">
												<label
													htmlFor="lateDeduction"
													className="d-flex align-items-center gap-3 mb-0">
													<div className="form-check form-switch m-0">
														<input
															className="form-check-input"
															type="checkbox"
															style={{
																top: "-12px",
															}}
															id="lateDeduction"
															checked={attSettings?.lateDeductionEnabled}
															onChange={(e) =>
																setAttSettings((p) => ({
																	...p,
																	lateDeductionEnabled: e.target.checked,
																}))
															}
														/>
													</div>
													{Translate[lang]?.late_deduction_enabled}
												</label>
											</div>
										</Col>
										<Col md={6}>
											<div className=" mb-3">
												<label
													htmlFor="earlyLeaveDeduction"
													className="d-flex align-items-center gap-3 mb-0">
													<div className="form-check form-switch mb-0">
														<input
															id="earlyLeaveDeduction"
															style={{
																top: "-12px",
															}}
															className="form-check-input"
															type="checkbox"
															checked={attSettings?.earlyLeaveDeductionEnabled}
															onChange={(e) =>
																setAttSettings((p) => ({
																	...p,
																	earlyLeaveDeductionEnabled: e.target.checked,
																}))
															}
														/>
													</div>
													{Translate[lang]?.early_leave_deduction_enabled}
												</label>
											</div>
										</Col>
									</Row>

									{attSettings.lateDeductionEnabled && (
										<TiersTable
											tiers={attSettings.lateTiers}
											type="late"
											lang={lang}
											onAdd={addTier}
											onRemove={removeTier}
											onUpdate={updateTier}
										/>
									)}

									{attSettings.earlyLeaveDeductionEnabled && (
										<TiersTable
											tiers={attSettings.earlyLeaveTiers}
											type="early"
											lang={lang}
											onAdd={addTier}
											onRemove={removeTier}
											onUpdate={updateTier}
										/>
									)}

									<div className="mt-4">
										<label className="text-label">
											{Translate[lang]?.excluded_employees}
										</label>
										<ScrollPagination
											isMulti
											Service={new EmployeesService()}
											value={selectedExcludedEmps}
											nameVal={"name"}
											setData={(e) => setSelectedExcludedEmps(e)}
										/>
									</div>

									<div className="mt-4">
										<Button
											variant="primary"
											onClick={saveAttSettings}
											disabled={savingAtt}>
											{savingAtt ? (
												<span className="spinner-border spinner-border-sm" />
											) : (
												Translate[lang]?.save
											)}
										</Button>
									</div>
								</>
							)}
						</Card.Body>
					</Card>
				</>
			)}

			{modal && (
				<TotalSalariesModal
					modal={modal}
					setShouldUpdate={setShouldUpdate}
					setModal={() => setModal(false)}
					params={{
						month: params.month.value,
						year: params.year.value,
					}}
				/>
			)}
		</Fragment>
	);
};

export default Salaries;
